import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { fetchChaseIframeUid, PaymentechPaymentType } from "@/shared/utils/PaymentechAPI";
import { QuoteAPI } from "@/shared/utils/QuoteAPI";
import { PublicConfig } from "@/shared/PublicConfig";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { LogUtils } from "@/shared/utils/LogUtils";
import { CookieUtils } from "@/shared/utils/CookieUtils";
import { QuoteStatus } from "@spotpetinsurance/spot-types/entities/PetQuote";

interface UseChasePaymentParams {
    quoteId?: string;
}

export type ChaseFinalizeStatus = "idle" | "finalizing" | "declined" | "failed" | "missing_token";

interface UseChasePaymentReturn {
    uid?: string;
    isPending: boolean;
    /**
     * True while a uid request is in flight — the first mint AND the refetch `reloadPaymentForm` fires.
     *
     * Gate the iframe on this. React Query keeps the PREVIOUS value in `data` across a refetch, so `uid`
     * still holds the single-use token the failed/declined attempt already consumed; mounting it renders
     * Chase's "Missing or Invalid UID!" page until the replacement lands.
     */
    isFetchingUid: boolean;
    error: unknown;
    finalizeStatus: ChaseFinalizeStatus;
    /** Mints a fresh uid and remounts the payment form (for declined / missing-token payments). */
    reloadPaymentForm: () => void;
}

const CHECKOUT_LOG_PREFIX = "canada_checkout";

// When a finalize response is lost/errors, re-check the authoritative quote status this many times before
// declaring failure. Slow policy binds can outlive the API Gateway's 29s integration timeout and land
// shortly after, so the window is wider than a "synchronous checkout" would suggest (env-tunable).
const FINALIZE_STATUS_POLL_ATTEMPTS = PublicConfig.PTZ_CA.FINALIZE_STATUS_POLL_ATTEMPTS;
const FINALIZE_STATUS_POLL_DELAY_MS = PublicConfig.PTZ_CA.FINALIZE_STATUS_POLL_DELAY_MS;

// Automatic retries of the finalize call, on top of the first attempt (so 2 = at most 3 requests).
const MAX_FINALIZE_RETRIES = 2;

// In non-production, Playwright can shrink the poll delay via a window global (see PTZ_CA.DEV_PARAMS) so
// the finalize status-poll tests don't wait the real interval. Ignored entirely in production.
function resolveFinalizePollDelayMs(): number {
    if (PublicConfig.ENVIRONMENT !== "production" && typeof window !== "undefined") {
        const override = (window as unknown as Record<string, unknown>)[PublicConfig.PTZ_CA.DEV_PARAMS.FINALIZE_POLL_DELAY_GLOBAL];
        if (typeof override === "number" && Number.isFinite(override) && override >= 0) return override;
    }
    return FINALIZE_STATUS_POLL_DELAY_MS;
}

// True when this quote has a finalize whose outcome may still be unresolved (the reload-survival marker).
// Read by the state/ref initializers below so a resumed mount renders GATED from the very first paint —
// setting that state synchronously inside the resume effect instead would cascade a render before paint
// (react-hooks/set-state-in-effect). document-guarded: harmless "idle" on any non-browser render pass.
function hasPendingFinalizeGate(quoteId?: string): boolean {
    if (!quoteId || typeof document === "undefined") return false;
    return CookieUtils.get(PublicConfig.PTZ_CA.COOKIE_FINALIZE_STARTED_KEY) === quoteId;
}

/**
 * Logs a CA checkout lifecycle event.
 *  - Always emits a DataDog RUM custom action (`canada_checkout_<name>`) for the analytics funnel.
 *  - Console echo is dev-only (LogUtils gates on non-production) — in prod it's just console noise.
 *  - Pass level "error"/"warn" to ALSO emit a DataDog Log at that severity. Reserve this for failures
 *    that AREN'T already auto-captured: failed HTTP requests are forwarded to Logs by DataDog itself
 *    (`forwardErrorsToLogs: true`), so don't double-log those — only client-side failures (no request)
 *    need the explicit Log.
 */
function logCheckoutEvent(name: string, context?: Record<string, unknown>, level: "info" | "warn" | "error" = "info") {
    LogUtils.devLog(`[${CHECKOUT_LOG_PREFIX}] ${name}`, level, context);
    AnalyticsUtils.sendDataDogAction(`${CHECKOUT_LOG_PREFIX}_${name}`, context);
    if (level !== "info") {
        AnalyticsUtils.sendDataDogLog(level, `${CHECKOUT_LOG_PREFIX}_${name}`, context);
    }
}

/**
 * Drives the Chase Hosted Payment Form (HPF) flow for the CA checkout step:
 * 1. Requests a single-use `uid` from the IPH/PetPartners backend (Credit Card).
 * 2. Registers the parent-page window event handlers that hpfParent.min.js invokes.
 * 3. On `completePayment` (card captured by Chase), finalizes the quote via our backend with
 *    `{ paymentToken: uid }`, which binds the policy. On success we navigate to /thankyou using the
 *    finalize POST's authoritative result — we never synthesize quoteStatus on the client.
 */
export function useChasePayment({ quoteId }: UseChasePaymentParams): UseChasePaymentReturn {
    const router = useRouter();
    const { updateAppState } = useAppLayerContext();
    // Seeded from the reload-survival marker: a resumed mount (page reloaded mid/after finalize) starts
    // GATED — "finalizing" hides the iframe on the first paint, and the fired-ref blocks completePayment —
    // so the resume effect below never needs a synchronous setState to engage the gate.
    const [finalizeStatus, setFinalizeStatus] = useState<ChaseFinalizeStatus>(() => (hasPendingFinalizeGate(quoteId) ? "finalizing" : "idle"));
    // Guards double-fire (Chase only sends completePayment once, but belt-and-braces).
    const finalizeFiredRef = useRef(hasPendingFinalizeGate(quoteId));
    // Ensures the reload-survival resume check runs at most once per mount.
    const resumeCheckedRef = useRef(false);

    // Fire-on-mount read of the uid. useQuery auto-runs and surfaces data/error/loading.
    // Only the quoteId is sent — the proxy resolves the amount/billing server-side from the quote.
    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch: refetchUid
    } = useQuery({
        queryKey: ["chase-hpf-uid", quoteId],
        queryFn: () =>
            fetchChaseIframeUid({
                // Non-null asserted: the query only runs when `enabled` (which requires quoteId) is true.
                quoteId: quoteId!,
                paymentType: PaymentechPaymentType.CreditCard
            }),
        enabled: !!quoteId,
        // The uid is single-use: keep it stable while the form is mounted (staleTime), but drop it
        // from cache on unmount (gcTime: 0) so navigating back then forward to Checkout requests a
        // fresh uid instead of reusing a consumed one ("Missing or Invalid UID").
        staleTime: Infinity,
        gcTime: 0,
        // Retry transient 5xx up to 3x with backoff;
        retry: (failureCount, err) => {
            const status = axios.isAxiosError(err) ? err.response?.status : undefined;
            const willRetry = failureCount < 3 && !!status && status >= 500;
            if (willRetry) {
                // Logged per attempt so we can measure how often the retry rescues a flaky load
                // (compare retry volume vs. the terminal uid_request_error rate).
                logCheckoutEvent("uid_request_retry", { quoteId, status, attempt: failureCount });
            }
            return willRetry;
        },
        retryDelay: attempt => Math.min(1000 * 2 ** attempt, 4000),
        refetchOnWindowFocus: false
    });

    const uid = data?.uid;

    // "A finalize is in flight" marker that survives reloads (see the resume-check effect below). Set when
    // finalize fires; cleared on any terminal outcome that permits a new attempt (finalized / declined).
    // The VALUE is the quoteId, so the marker only ever gates the quote whose finalize is actually
    // unresolved — a reset or a new quote in the same session must not inherit another quote's gate.
    // Known limitation (accepted): single slot, so a second quote's finalize evicts the first quote's
    // marker. Residual risk is negligible (synchronous checkout + the finalized→thank-you redirect); the
    // durable fix for the class is server-side finalize idempotency, tracked separately.
    const setFinalizeStarted = useCallback(() => {
        if (quoteId) CookieUtils.set(PublicConfig.PTZ_CA.COOKIE_FINALIZE_STARTED_KEY, quoteId);
    }, [quoteId]);
    const clearFinalizeStarted = useCallback(() => CookieUtils.remove(PublicConfig.PTZ_CA.COOKIE_FINALIZE_STARTED_KEY), []);

    const goToThankYou = useCallback(() => {
        clearFinalizeStarted();
        const params = new URLSearchParams({ uw: PublicConfig.PTZ_CA.UNDERWRITER, quoteId: quoteId ?? "" });
        router.push(`${PublicConfig.BASE_PATH}/forms/${PublicConfig.PTZ_CA.FORM_ID}/thankyou?${params.toString()}`);
    }, [clearFinalizeStarted, quoteId, router]);

    // Ask the server for the authoritative quote status a bounded number of times. Used when a finalize
    // response is lost/errors: Orca may have bound the policy (charge succeeded, response didn't), and the
    // status is the source of truth — not the finalize call's return. Mirrors the US poll model
    // (useQuoteStatus). Returns a terminal status, or undefined if it never resolves in the window.
    const pollQuoteStatus = useCallback(async (): Promise<QuoteStatus | undefined> => {
        if (!quoteId) return undefined;
        const api = new QuoteAPI(PublicConfig.PTZ_CA.UNDERWRITER);
        for (let attempt = 0; attempt < FINALIZE_STATUS_POLL_ATTEMPTS; attempt++) {
            try {
                const status = (await api.getQuote(quoteId))?.quoteStatus;
                if (status === "finalized" || status === "failed-authorization") return status;
            } catch {
                // Transient read failure — keep polling; a genuinely stuck read falls through to undefined.
            }
            if (attempt < FINALIZE_STATUS_POLL_ATTEMPTS - 1) {
                await new Promise(resolve => setTimeout(resolve, resolveFinalizePollDelayMs()));
            }
        }
        return undefined;
    }, [quoteId]);

    const { mutate: finalize } = useMutation({
        mutationFn: (paymentToken: string) => new QuoteAPI(PublicConfig.PTZ_CA.UNDERWRITER).finalizeQuote(quoteId!, false, { paymentToken }),
        // Finalize is idempotent server-side (re-calling after success returns the same order), so
        // auto-retrying is worthwhile: the dominant failure mode is a slow policy bind tripping the 29s
        // API Gateway integration timeout (504), which a short wait often clears on its own.
        //
        // Retry ONLY on a 5xx status. A 4xx (e.g. an invalid or already-consumed token) fails
        // deterministically and would just fail again; a failure with NO response (network drop, client
        // abort) means the charge may already be in flight, so it skips straight to onError's status
        // poll — which reconciles by READING the quote rather than re-firing a charge attempt.
        retry: (failureCount, err) => {
            // failureCount = retries already performed (0 on the first failure), so this allows exactly
            // MAX_FINALIZE_RETRIES retries — 3 requests in total.
            if (failureCount >= MAX_FINALIZE_RETRIES) return false;
            if (!axios.isAxiosError(err)) return false;
            const status = err.response?.status;
            if (status === undefined || status < 500) return false;
            // Logged per attempt so the auto-retry's rescue rate is measurable against the terminal
            // finalize_error rate (same pattern as uid_request_retry above).
            logCheckoutEvent("finalize_retry", { quoteId, status, attempt: failureCount });
            return true;
        },
        // Exponential backoff: 1s, then 2s — ~3s of waiting worst-case on top of the request round-trips.
        retryDelay: attempt => Math.min(1000 * 2 ** attempt, 4000),
        onSuccess: finalizedQuote => {
            if (finalizedQuote?.quoteStatus === "finalized") {
                logCheckoutEvent("finalize_success", { quoteId });
                // The finalize POST is the authoritative "policy bound" signal — navigate straight to
                // thank-you, which loads the order from the server.
                goToThankYou();
                return;
            }
            updateAppState({ showFinalLoader: false });
            // An explicit decline means no charge stands — safe to drop the gate and offer a fresh form.
            if (finalizedQuote?.quoteStatus === "failed-authorization") {
                logCheckoutEvent("finalize_declined", { quoteId, quoteStatus: finalizedQuote.quoteStatus });
                clearFinalizeStarted();
                setFinalizeStatus("declined");
                return;
            }
            // Any other 200 status is unexpected (the backend returns only finalized/failed-authorization
            // today). Treat it like an unknown outcome: keep the gate, route to the call-center state —
            // never un-gate a charge that may still be settling.
            logCheckoutEvent("finalize_error", { quoteId, message: `unexpected 200 quoteStatus: ${finalizedQuote?.quoteStatus}` }, "error");
            setFinalizeStatus("failed");
        },
        onError: async err => {
            // RUM action only: the finalize HTTP failure is auto-forwarded to Logs (forwardErrorsToLogs).
            logCheckoutEvent("finalize_error", { quoteId, message: String(err) });
            // The finalize call failed, but Orca may have bound the policy before the response was lost.
            // Re-check the authoritative status before showing an error or inviting a re-charge; keep the
            // final loader up (finalizeStatus stays "finalizing") while we poll.
            const status = await pollQuoteStatus();
            if (status === "finalized") {
                logCheckoutEvent("finalize_recovered", { quoteId });
                goToThankYou();
                return;
            }
            updateAppState({ showFinalLoader: false });
            if (status === "failed-authorization") {
                logCheckoutEvent("finalize_declined", { quoteId, quoteStatus: status });
                clearFinalizeStarted();
                setFinalizeStatus("declined");
                return;
            }
            // Still unresolved after polling — genuinely unknown. Keep the cookie set so a reload stays
            // gated (call-center only, no fresh payment form). Surface the failed state.
            setFinalizeStatus("failed");
        }
    });

    // RUM action only (for the funnel): the failed uid request is auto-forwarded to Logs by DataDog
    // (forwardErrorsToLogs), so we don't emit a duplicate explicit error log here.
    useEffect(() => {
        if (error) logCheckoutEvent("uid_request_error", { quoteId, message: String(error) });
    }, [error, quoteId]);

    // Register the HPF parent-page event handlers and clean them up on unmount.
    useEffect(() => {
        // hpfParent.min.js invokes these lifecycle hooks, so they must exist — but ready/start_payment
        // are high-frequency pings we don't need in telemetry, so keep them as no-ops.
        window.hpfReady = () => {};
        window.startPayment = () => {};
        window.handlePaymentErrors = e => {
            // In-frame validation/decline errors — Chase renders these and allows retries in the iframe.
            logCheckoutEvent("payment_error", { error: e });
        };
        window.completePayment = d => {
            logCheckoutEvent("payment_complete");
            if (!quoteId || finalizeFiredRef.current) return;
            // Prefer the uid echoed in the Chase response; fall back to the one we minted.
            const echoedUid = !!d && typeof d === "object" && "uid" in d && typeof d.uid === "string" ? d.uid : undefined;
            const paymentToken = echoedUid ?? uid;
            if (!paymentToken) {
                // Keep the explicit error log: this is a client-side failure (no HTTP request fired), so
                // forwardErrorsToLogs won't capture it — yet the customer's card was just captured, so
                // it's high-severity and needs to be searchable/alertable in Logs.
                logCheckoutEvent("finalize_error_token", { quoteId, message: "completePayment fired but no uid available" }, "error");
                // No token to finalize with, so the customer must re-enter payment against a fresh uid.
                // Route to a state whose CTA reloads the form.
                setFinalizeStatus("missing_token");
                return;
            }
            finalizeFiredRef.current = true;
            setFinalizeStarted();
            setFinalizeStatus("finalizing");
            updateAppState({ showFinalLoader: true });
            finalize(paymentToken);
        };
        window.cancelPayment = () => logCheckoutEvent("payment_cancelled");

        return () => {
            delete window.hpfReady;
            delete window.startPayment;
            delete window.handlePaymentErrors;
            delete window.completePayment;
            delete window.cancelPayment;
        };
    }, [quoteId, uid, finalize, updateAppState, setFinalizeStarted]);

    // Reload-survival: if a finalize was already started (cookie present on a fresh mount = the page was
    // reloaded mid/after finalize), do NOT hand back a fresh payment form — that would let the customer pay
    // twice while the first attempt may still be settling. Reconcile against the authoritative status
    // instead: finalized → thank-you, declined → declined, otherwise → the call-center "failed" state.
    // (A finalized quote is also caught by the formController → /thankyou redirect; this is the in-flight
    // backstop and the client half of the double-charge guard — the durable fix is server-side idempotency.)
    useEffect(() => {
        if (resumeCheckedRef.current || !quoteId) return;
        const startedQuoteId = CookieUtils.get(PublicConfig.PTZ_CA.COOKIE_FINALIZE_STARTED_KEY);
        if (!startedQuoteId) return;
        if (startedQuoteId !== quoteId) {
            // The marker belongs to a DIFFERENT quote (reset / new quote in the same session). This quote
            // has no unresolved finalize, so don't gate it — but do NOT clear the marker either: it's the
            // live double-charge gate for the OTHER quote (whose finalize outcome is still unknown), and
            // the customer may return to that quote later. Foreign markers are inert here by the id check.
            return;
        }
        resumeCheckedRef.current = true;
        finalizeFiredRef.current = true; // already seeded true on a resumed mount; re-asserted for a late quoteId
        logCheckoutEvent("finalize_resume_check", { quoteId }, "warn");
        // No setFinalizeStatus here: the useState initializer (same gate check) already seeded "finalizing",
        // so the iframe is hidden from the first paint — a sync setState in an effect cascades a render
        // (react-hooks/set-state-in-effect). The async outcome sets below run post-await, which is fine.
        updateAppState({ showFinalLoader: true });
        (async () => {
            const status = await pollQuoteStatus();
            if (status === "finalized") {
                logCheckoutEvent("finalize_recovered", { quoteId, resumed: true });
                goToThankYou();
                return;
            }
            updateAppState({ showFinalLoader: false });
            if (status === "failed-authorization") {
                logCheckoutEvent("finalize_declined", { quoteId, quoteStatus: status });
                clearFinalizeStarted();
                setFinalizeStatus("declined");
                return;
            }
            // Still unresolved on resume — keep the cookie so a further reload stays gated; call-center only.
            setFinalizeStatus("failed");
        })();
        // resumeCheckedRef makes the body a no-op after the first run, so re-runs from the recreated
        // function deps below are harmless — it still executes at most once per mount.
    }, [quoteId, pollQuoteStatus, goToThankYou, clearFinalizeStarted, updateAppState]);

    const reloadPaymentForm = () => {
        finalizeFiredRef.current = false;
        setFinalizeStatus("idle");
        // The consumed uid stays in `data` until this resolves, so consumers must keep the iframe
        // unmounted while `isFetchingUid` is true rather than remounting it against the old token.
        refetchUid();
    };

    return { uid, isPending: isLoading, isFetchingUid: isFetching, error, finalizeStatus, reloadPaymentForm };
}
